import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Copy, Eye, Pencil, Plus, RotateCcw, Save, Trash2, X } from 'lucide-react';
import { apiDelete, apiGet, apiPost, apiPut } from '../utils/api';
import { useLocation, useNavigate } from 'react-router-dom';

type LevelType = 'SINGLES' | 'DOUBLES';

type PlayerMeta = {
  id: string;
  name: string;
  singlesLevel?: string | null;
  doublesLevel?: string | null;
};

type ListItem = {
  id: string;
  status: 'DRAFT' | 'POSTED' | string;
  effectiveDate: string;
  itemsCount: number;
  notes?: string | null;
  postedAt?: string | null;
  createdAt: string;
};

type DocumentRow = {
  id?: string;
  playerId: string;
  levelType: LevelType;
  previousLevel?: string | null;
  newLevel: string;
  applyElo: boolean;
};

type DocumentDetails = ListItem & {
  items: Array<{
    id: string;
    playerId: string;
    levelType: LevelType;
    previousLevel?: string | null;
    newLevel: string;
    applyElo?: boolean;
    player: PlayerMeta;
  }>;
};

const DEFAULT_SINGLES_LEVELS = ['S-4F', 'S-4M', 'S-5', 'S-6', 'S-7', 'S-8', 'S-9'];
const DEFAULT_DOUBLES_LEVELS = ['D-Mixt', 'D-6', 'D-6.5', 'D-7', 'D-ProAm'];
const ELO_BY_LEVEL: Record<string, number> = {
  'S-4F': 1200,
  'S-4M': 1500,
  'S-5': 1800,
  'S-6': 2100,
  'S-7': 2400,
  'S-8': 2700,
  'S-9': 3000,
  'D-MIXT': 1200,
  'D-6': 1800,
  'D-6.5': 1800,
  'D-7': 2400,
  'D-PROAM': 3000
};

const getStatusLabel = (status?: string) => {
  if (status === 'DRAFT') return 'Черновик';
  if (status === 'POSTED') return 'Проведен';
  return status || '-';
};

const getDocumentNumber = (documentId?: string | null): string => {
  const normalized = String(documentId || '').trim();
  if (!normalized) return '—';
  return `№${normalized.slice(-8).toUpperCase()}`;
};

const getTodayLocalDateInput = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const emptyRow = (): DocumentRow => ({
  playerId: '',
  levelType: 'SINGLES',
  previousLevel: null,
  newLevel: '',
  applyElo: false
});

const resolveEloByLevel = (level?: string | null): number | null => {
  const normalized = String(level || '').trim().toUpperCase();
  if (!normalized) return null;
  return ELO_BY_LEVEL[normalized] ?? null;
};

const isSelectableLevel = (level?: string | null): boolean => {
  const normalized = String(level || '').trim().toUpperCase();
  if (!normalized) return false;
  return normalized !== 'UNKNOWN';
};

const PlayerLevelChangeDocuments: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const openedByQueryRef = useRef<string | null>(null);
  const [documents, setDocuments] = useState<ListItem[]>([]);
  const [players, setPlayers] = useState<PlayerMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [copyingId, setCopyingId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorMode, setEditorMode] = useState<'create' | 'edit' | 'view'>('create');
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(null);
  const [editingDocumentStatus, setEditingDocumentStatus] = useState<'DRAFT' | 'POSTED' | string>('DRAFT');
  const [formEffectiveDate, setFormEffectiveDate] = useState(getTodayLocalDateInput());
  const [formNotes, setFormNotes] = useState('');
  const [formRows, setFormRows] = useState<DocumentRow[]>([emptyRow()]);
  const [currentEloByPair, setCurrentEloByPair] = useState<Record<string, number | null>>({});
  const eloSnapshotTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const playerById = useMemo(() => new Map(players.map((p) => [p.id, p])), [players]);
  const singlesLevelOptions = useMemo(() => {
    const set = new Set<string>(DEFAULT_SINGLES_LEVELS);
    players.forEach((p) => {
      const level = String(p.singlesLevel || '').trim();
      if (isSelectableLevel(level)) set.add(level);
    });
    const known = DEFAULT_SINGLES_LEVELS.filter((lvl) => set.has(lvl));
    const extras = [...set].filter((lvl) => !DEFAULT_SINGLES_LEVELS.includes(lvl) && isSelectableLevel(lvl));
    return [...known, ...extras];
  }, [players]);
  const doublesLevelOptions = useMemo(() => {
    const set = new Set<string>(DEFAULT_DOUBLES_LEVELS);
    players.forEach((p) => {
      const level = String(p.doublesLevel || '').trim();
      if (isSelectableLevel(level)) set.add(level);
    });
    const known = DEFAULT_DOUBLES_LEVELS.filter((lvl) => set.has(lvl));
    const extras = [...set].filter((lvl) => !DEFAULT_DOUBLES_LEVELS.includes(lvl) && isSelectableLevel(lvl));
    return [...known, ...extras];
  }, [players]);

  const loadDocuments = async (searchValue?: string) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('limit', '100');
      if ((searchValue ?? search).trim()) {
        params.set('search', (searchValue ?? search).trim());
      }
      const data = await apiGet<{ documents: ListItem[] }>(`/player-level-change-documents?${params.toString()}`);
      setDocuments(Array.isArray(data.documents) ? data.documents : []);
    } catch (error) {
      console.error('Ошибка загрузки документов изменения уровня:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPlayers = async () => {
    try {
      const data = await apiGet<{ players: PlayerMeta[] }>('/player-level-change-documents/meta/players');
      setPlayers(Array.isArray(data.players) ? data.players : []);
    } catch (error) {
      console.error('Ошибка загрузки игроков для документа изменения уровня:', error);
      setPlayers([]);
    }
  };

  useEffect(() => {
    loadDocuments();
    loadPlayers();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const documentId = String(params.get('documentId') || '').trim();
    const modeRaw = String(params.get('mode') || '').trim().toLowerCase();
    const playerId = String(params.get('playerId') || '').trim();
    const mode: 'edit' | 'view' = modeRaw === 'view' ? 'view' : 'edit';

    if (documentId) {
      const queryKey = `${documentId}:${mode}`;
      if (openedByQueryRef.current === queryKey) return;
      openedByQueryRef.current = queryKey;
      openDocument(documentId, mode).finally(() => {
        navigate('/documents/player-level-changes', { replace: true });
      });
      return;
    }

    if (modeRaw === 'create' && playerId) {
      const queryKey = `create:${playerId}`;
      if (openedByQueryRef.current === queryKey) return;
      openedByQueryRef.current = queryKey;
      openCreateEditor(playerId);
      navigate('/documents/player-level-changes', { replace: true });
    }
  }, [location.search]);

  const openCreateEditor = (playerId?: string) => {
    const normalizedPlayerId = String(playerId || '').trim();
    const player = normalizedPlayerId ? playerById.get(normalizedPlayerId) : null;
    setEditorMode('create');
    setEditingDocumentId(null);
    setEditingDocumentStatus('DRAFT');
    setFormEffectiveDate(getTodayLocalDateInput());
    setFormNotes('');
    setFormRows([
      normalizedPlayerId
        ? {
            playerId: normalizedPlayerId,
            levelType: 'SINGLES',
            previousLevel: player?.singlesLevel || null,
            newLevel: '',
            applyElo: false
          }
        : emptyRow()
    ]);
    setCurrentEloByPair({});
    setEditorOpen(true);
  };

  const openDocument = async (documentId: string, mode: 'edit' | 'view') => {
    try {
      const data = await apiGet<DocumentDetails>(`/player-level-change-documents/${documentId}`);
      setEditorMode(mode);
      setEditingDocumentId(data.id);
      setEditingDocumentStatus(data.status);
      setFormEffectiveDate(new Date(data.effectiveDate).toISOString().slice(0, 10));
      setFormNotes(data.notes || '');
      setFormRows(
        data.items.length > 0
          ? data.items.map((item) => ({
              id: item.id,
              playerId: item.playerId,
              levelType: item.levelType,
              previousLevel: item.previousLevel || null,
              newLevel: item.newLevel,
              applyElo: item.applyElo === true
            }))
          : [emptyRow()]
      );
      setCurrentEloByPair({});
      setEditorOpen(true);
    } catch (error: any) {
      console.error('Ошибка открытия документа изменения уровня:', error);
      alert(`Ошибка: ${error.message || 'Не удалось открыть документ'}`);
    }
  };

  const openEditEditor = (documentId: string) => openDocument(documentId, 'edit');
  const openViewEditor = (documentId: string) => openDocument(documentId, 'view');
  const openCreateByCopy = async (documentId: string) => {
    try {
      setCopyingId(documentId);
      const data = await apiGet<DocumentDetails>(`/player-level-change-documents/${documentId}`);
      setEditorMode('create');
      setEditingDocumentId(null);
      setEditingDocumentStatus('DRAFT');
      setFormEffectiveDate(new Date(data.effectiveDate).toISOString().slice(0, 10));
      setFormNotes(data.notes || '');
      setFormRows(
        data.items.length > 0
          ? data.items.map((item) => ({
              playerId: item.playerId,
              levelType: item.levelType,
              previousLevel: item.previousLevel || null,
              newLevel: item.newLevel,
              applyElo: item.applyElo === true
            }))
          : [emptyRow()]
      );
      setCurrentEloByPair({});
      setEditorOpen(true);
    } catch (error: any) {
      console.error('Ошибка копирования документа изменения уровня:', error);
      alert(`Ошибка: ${error.message || 'Не удалось создать копию документа'}`);
    } finally {
      setCopyingId(null);
    }
  };

  const updateRow = (idx: number, patch: Partial<DocumentRow>) => {
    setFormRows((prev) =>
      prev.map((row, rowIdx) => {
        if (rowIdx !== idx) return row;
        const next = { ...row, ...patch };
        if (patch.playerId !== undefined || patch.levelType !== undefined) {
          const player = playerById.get(next.playerId);
          next.previousLevel =
            next.levelType === 'SINGLES'
              ? player?.singlesLevel || null
              : player?.doublesLevel || null;
        }
        return next;
      })
    );
  };

  const addRow = () => {
    setFormRows((prev) => [...prev, emptyRow()]);
  };

  const removeRow = (idx: number) => {
    setFormRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, rowIdx) => rowIdx !== idx);
    });
  };

  const duplicateRow = (idx: number) => {
    setFormRows((prev) => {
      const source = prev[idx];
      if (!source) return prev;
      const nextRow: DocumentRow = {
        playerId: source.playerId,
        levelType: source.levelType,
        previousLevel: source.previousLevel || null,
        newLevel: source.newLevel,
        applyElo: source.applyElo === true
      };
      return [...prev.slice(0, idx + 1), nextRow, ...prev.slice(idx + 1)];
    });
  };

  useEffect(() => {
    if (!editorOpen) return;
    if (!formEffectiveDate) {
      setCurrentEloByPair({});
      return;
    }

    const items = formRows
      .map((row) => ({
        playerId: String(row.playerId || '').trim(),
        levelType: row.levelType
      }))
      .filter((row) => row.playerId);

    if (items.length < 1) {
      setCurrentEloByPair({});
      return;
    }

    if (eloSnapshotTimeoutRef.current) clearTimeout(eloSnapshotTimeoutRef.current);
    eloSnapshotTimeoutRef.current = setTimeout(async () => {
      try {
        const data = await apiPost<{
          snapshots: Array<{ playerId: string; levelType: LevelType; currentElo: number | null }>;
        }>('/player-level-change-documents/elo-snapshot', {
          effectiveDate: formEffectiveDate,
          items
        });
        const next: Record<string, number | null> = {};
        for (const snap of data.snapshots || []) {
          next[`${snap.playerId}::${snap.levelType}`] = typeof snap.currentElo === 'number' ? snap.currentElo : null;
        }
        setCurrentEloByPair(next);
      } catch (error) {
        console.error('Ошибка загрузки текущего Elo на дату документа:', error);
        setCurrentEloByPair({});
      }
    }, 250);

    return () => {
      if (eloSnapshotTimeoutRef.current) clearTimeout(eloSnapshotTimeoutRef.current);
    };
  }, [editorOpen, formEffectiveDate, formRows]);

  const isViewOnly = editorMode === 'view';
  const isDraftEditable = (editorMode === 'edit' && editingDocumentStatus === 'DRAFT') || editorMode === 'create';
  const isFormReadOnly = isViewOnly || (editorMode === 'edit' && editingDocumentStatus !== 'DRAFT');
  const isApplyEloEditable = !isViewOnly;

  const buildDocumentPayload = () => ({
    effectiveDate: formEffectiveDate,
    notes: formNotes || null,
    items: formRows.map((row) => ({
      playerId: row.playerId,
      levelType: row.levelType,
      newLevel: row.newLevel,
      applyElo: row.applyElo === true
    }))
  });

  const saveDocument = async () => {
    if (!formEffectiveDate) {
      alert('Укажите дату документа.');
      return;
    }
    if (formRows.length < 1) {
      alert('Документ должен содержать минимум одну строку.');
      return;
    }

    try {
      setSaving(true);
      const payload = buildDocumentPayload();

      if (editingDocumentId) {
        await apiPut(`/player-level-change-documents/${editingDocumentId}`, payload);
      } else {
        await apiPost('/player-level-change-documents', payload);
      }
      await loadDocuments();
      setEditorOpen(false);
    } catch (error: any) {
      console.error('Ошибка сохранения документа изменения уровня:', error);
      alert(`Ошибка: ${error.message || 'Не удалось сохранить документ'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleChangeStatus = async (documentId: string, nextStatus: 'DRAFT' | 'POSTED') => {
    const target = documents.find((d) => d.id === documentId);
    const isRepost = nextStatus === 'POSTED' && String(target?.status || '').toUpperCase() === 'POSTED';
    const actionText = nextStatus === 'POSTED'
      ? (isRepost ? 'перепровести' : 'провести')
      : 'отменить проведение (в Черновик)';
    const confirmed = window.confirm(
      `Подтвердите: ${actionText} документ от ${new Date(target?.effectiveDate || Date.now()).toLocaleDateString('ru-RU')}?`
    );
    if (!confirmed) return;

    try {
      setStatusUpdatingId(documentId);
      const updated = await apiPut<ListItem>(`/player-level-change-documents/${documentId}/status`, {
        status: nextStatus
      });
      setDocuments((prev) => prev.map((doc) => (doc.id === documentId ? { ...doc, status: updated.status, postedAt: updated.postedAt } : doc)));
    } catch (error: any) {
      console.error('Ошибка изменения статуса документа изменения уровня:', error);
      alert(`Ошибка: ${error.message || 'Не удалось изменить статус документа'}`);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleDelete = async (documentId: string) => {
    const target = documents.find((d) => d.id === documentId);
    if (target?.status !== 'DRAFT') {
      alert('Удаление доступно только для документов в статусе Черновик.');
      return;
    }
    const confirmed = window.confirm('Удалить документ изменения уровня?');
    if (!confirmed) return;
    try {
      setDeletingId(documentId);
      await apiDelete(`/player-level-change-documents/${documentId}`);
      setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
    } catch (error: any) {
      console.error('Ошибка удаления документа изменения уровня:', error);
      alert(`Ошибка: ${error.message || 'Не удалось удалить документ'}`);
    } finally {
      setDeletingId(null);
    }
  };

  const handlePostFromEditor = async () => {
    const confirmed = window.confirm(
      editingDocumentStatus === 'POSTED'
        ? 'Перепровести текущий документ?'
        : 'Провести текущий документ?'
    );
    if (!confirmed) return;
    try {
      setSaving(true);
      if (editorMode === 'create') {
        if (!formEffectiveDate) {
          alert('Укажите дату документа.');
          return;
        }
        if (formRows.length < 1) {
          alert('Документ должен содержать минимум одну строку.');
          return;
        }
        const created = await apiPost<{ id: string }>('/player-level-change-documents', buildDocumentPayload());
        const createdId = String(created?.id || '').trim();
        if (!createdId) {
          throw new Error('Не удалось получить ID созданного документа');
        }
        await apiPut<ListItem>(`/player-level-change-documents/${createdId}/status`, {
          status: 'POSTED',
          applyEloOverrides: formRows.map((row) => ({
            itemId: row.id,
            applyElo: row.applyElo === true
          })).filter((row) => Boolean(row.itemId))
        });
      } else if (editingDocumentStatus === 'DRAFT') {
        if (!editingDocumentId) return;
        if (!formEffectiveDate) {
          alert('Укажите дату документа.');
          return;
        }
        if (formRows.length < 1) {
          alert('Документ должен содержать минимум одну строку.');
          return;
        }
        // Для черновика сначала сохраняем форму, затем проводим.
        await apiPut(`/player-level-change-documents/${editingDocumentId}`, buildDocumentPayload());
        await apiPut<ListItem>(`/player-level-change-documents/${editingDocumentId}/status`, {
          status: 'POSTED',
          applyEloOverrides: formRows.map((row) => ({
            itemId: row.id,
            applyElo: row.applyElo === true
          })).filter((row) => Boolean(row.itemId))
        });
      } else if (editingDocumentId) {
        await apiPut<ListItem>(`/player-level-change-documents/${editingDocumentId}/status`, {
          status: 'POSTED',
          applyEloOverrides: formRows.map((row) => ({
            itemId: row.id,
            applyElo: row.applyElo === true
          })).filter((row) => Boolean(row.itemId))
        });
      }
      await loadDocuments();
      setEditingDocumentStatus('POSTED');
      setEditorOpen(false);
    } catch (error: any) {
      console.error('Ошибка проведения документа из формы:', error);
      alert(`Ошибка: ${error.message || 'Не удалось провести документ'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleUnpostFromEditor = async () => {
    if (!editingDocumentId) return;
    const confirmed = window.confirm('Снять документ с проведения и перевести в Черновик?');
    if (!confirmed) return;
    try {
      setSaving(true);
      await apiPut<ListItem>(`/player-level-change-documents/${editingDocumentId}/status`, {
        status: 'DRAFT'
      });
      await loadDocuments();
      await openDocument(editingDocumentId, 'edit');
    } catch (error: any) {
      console.error('Ошибка снятия документа с проведения:', error);
      alert(`Ошибка: ${error.message || 'Не удалось снять документ с проведения'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Журнал изменения уровня игрока</h1>
        <button
          onClick={openCreateEditor}
          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm inline-flex items-center gap-1"
        >
          <Plus size={16} />
          Новый документ
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по ФИО или примечанию"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button onClick={() => loadDocuments(search)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
            Найти
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Загрузка...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Дата</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">№</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Действует с</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Статус</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Строк</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Действия</th>
                </tr>
              </thead>
              <tbody>
                {documents.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      Документы изменения уровня не найдены.
                    </td>
                  </tr>
                ) : (
                  documents.map((doc) => (
                    <tr key={doc.id} className="border-t border-gray-100">
                      <td className="px-4 py-2">{new Date(doc.createdAt).toLocaleString('ru-RU')}</td>
                      <td className="px-4 py-2">{getDocumentNumber(doc.id)}</td>
                      <td className="px-4 py-2">{new Date(doc.effectiveDate).toLocaleDateString('ru-RU')}</td>
                      <td className="px-4 py-2">{getStatusLabel(doc.status)}</td>
                      <td className="px-4 py-2 text-right">{doc.itemsCount}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openViewEditor(doc.id)}
                            className="p-1.5 rounded text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                            disabled={deletingId === doc.id || statusUpdatingId === doc.id}
                            title="Просмотр"
                            aria-label="Просмотр"
                          >
                            <Eye size={16} />
                          </button>
                          {doc.status === 'DRAFT' && (
                            <button
                              onClick={() => openEditEditor(doc.id)}
                              className="p-1.5 rounded text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
                              disabled={deletingId === doc.id || statusUpdatingId === doc.id}
                              title="Редактировать"
                              aria-label="Редактировать"
                            >
                              <Pencil size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => handleChangeStatus(doc.id, 'POSTED')}
                            className="p-1.5 rounded text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                            disabled={statusUpdatingId === doc.id || deletingId === doc.id}
                            title={doc.status === 'POSTED' ? 'Перепровести' : 'Провести'}
                            aria-label={doc.status === 'POSTED' ? 'Перепровести' : 'Провести'}
                          >
                            <Check size={16} />
                          </button>
                          {doc.status === 'POSTED' && (
                            <button
                              onClick={() => handleChangeStatus(doc.id, 'DRAFT')}
                              className="p-1.5 rounded text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                              disabled={statusUpdatingId === doc.id || deletingId === doc.id}
                              title="Отменить (в Черновик)"
                              aria-label="Отменить (в Черновик)"
                            >
                              <RotateCcw size={16} />
                            </button>
                          )}
                          {doc.status === 'DRAFT' && (
                            <button
                              onClick={() => openCreateByCopy(doc.id)}
                              className="p-1.5 rounded text-violet-700 hover:bg-violet-100 disabled:opacity-50"
                              disabled={deletingId === doc.id || statusUpdatingId === doc.id || copyingId === doc.id}
                              title="📄 Создать копированием"
                              aria-label="Создать копированием"
                            >
                              <span className="text-[15px] leading-none">📄</span>
                            </button>
                          )}
                          {doc.status === 'DRAFT' && (
                            <button
                              onClick={() => handleDelete(doc.id)}
                              className="p-1.5 rounded text-red-700 hover:bg-red-100 disabled:opacity-50"
                              disabled={deletingId === doc.id || statusUpdatingId === doc.id || copyingId === doc.id}
                              title="Удалить"
                              aria-label="Удалить"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editorOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {editorMode === 'create'
                  ? 'Новый документ изменения уровня'
                  : editorMode === 'view'
                    ? 'Просмотр документа изменения уровня'
                    : 'Редактирование документа изменения уровня'}
              </h2>
              <button
                onClick={() => setEditorOpen(false)}
                className="p-1.5 rounded text-gray-500 hover:bg-gray-100"
                title="Закрыть"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-4 space-y-4 overflow-y-auto min-h-0">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="text-sm">
                  <span className="block text-gray-600 mb-1">Номер документа</span>
                  <input
                    type="text"
                    value={getDocumentNumber(editingDocumentId)}
                    disabled
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-700"
                  />
                </label>
                <label className="text-sm">
                  <span className="block text-gray-600 mb-1">Дата документа</span>
                  <input
                    type="date"
                    value={formEffectiveDate}
                    onChange={(e) => setFormEffectiveDate(e.target.value)}
                    disabled={isFormReadOnly}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </label>
                <label className="text-sm md:col-span-1">
                  <span className="block text-gray-600 mb-1">Примечание</span>
                  <input
                    type="text"
                    value={formNotes}
                    onChange={(e) => setFormNotes(e.target.value)}
                    disabled={isFormReadOnly}
                    placeholder="Комментарий (опционально)"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </label>
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Игрок</th>
                      <th className="px-3 py-2 text-left">Тип уровня</th>
                      <th className="px-3 py-2 text-left">Предыдущий</th>
                      <th className="px-3 py-2 text-left">Новый</th>
                      <th className="px-3 py-2 text-left">Elo</th>
                      <th className="px-3 py-2 text-left">#</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formRows.map((row, idx) => (
                      <tr key={`${row.id || 'new'}-${idx}`} className="border-t border-gray-100">
                        <td className="px-3 py-2">
                          <select
                            value={row.playerId}
                            onChange={(e) => updateRow(idx, { playerId: e.target.value })}
                            disabled={isFormReadOnly}
                            className="w-full border border-gray-300 rounded-md px-2 py-1.5"
                          >
                            <option value="">Выберите игрока</option>
                            {players.map((player) => (
                              <option key={player.id} value={player.id}>
                                {player.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={row.levelType}
                            onChange={(e) => updateRow(idx, { levelType: e.target.value as LevelType })}
                            disabled={isFormReadOnly}
                            className="w-full border border-gray-300 rounded-md px-2 py-1.5"
                          >
                            <option value="SINGLES">Одиночный</option>
                            <option value="DOUBLES">Парный</option>
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-gray-700">{row.previousLevel || '-'}</span>
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={row.newLevel}
                            onChange={(e) => updateRow(idx, { newLevel: e.target.value })}
                            disabled={isFormReadOnly}
                            className="w-full border border-gray-300 rounded-md px-2 py-1.5"
                          >
                            <option value="">Выберите уровень</option>
                            {(row.levelType === 'SINGLES' ? singlesLevelOptions : doublesLevelOptions).map((level) => (
                              <option key={level} value={level}>
                                {level}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          {(() => {
                            const pairKey = `${row.playerId}::${row.levelType}`;
                            const targetElo = resolveEloByLevel(row.newLevel);
                            const currentElo = currentEloByPair[pairKey];
                            return (
                          <div className="flex items-center gap-2 whitespace-nowrap">
                            <span className="text-gray-700 min-w-[120px] text-xs">
                              {targetElo ?? '—'}
                              <span className="text-gray-400"> / </span>
                              <span className="text-gray-500">тек: {typeof currentElo === 'number' ? currentElo : '—'}</span>
                            </span>
                            <label className="inline-flex items-center text-xs text-gray-600">
                              <input
                                type="checkbox"
                                checked={row.applyElo === true}
                                onChange={(e) => updateRow(idx, { applyElo: e.target.checked })}
                                disabled={!isApplyEloEditable}
                                title="Применить установку Elo"
                              />
                            </label>
                          </div>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => duplicateRow(idx)}
                            disabled={isFormReadOnly}
                            className="mr-1 p-1.5 rounded text-indigo-700 hover:bg-indigo-100 disabled:opacity-40"
                            title="Создать копированием"
                            aria-label="Создать копированием"
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            onClick={() => removeRow(idx)}
                            disabled={formRows.length <= 1 || isFormReadOnly}
                            className="p-1.5 rounded text-red-700 hover:bg-red-100 disabled:opacity-40"
                            title="Удалить строку"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {!isFormReadOnly && (
                <button
                  onClick={addRow}
                  className="px-3 py-1.5 border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 text-sm inline-flex items-center gap-1"
                >
                  <Plus size={14} />
                  Добавить строку
                </button>
              )}
            </div>

            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
              <button
                onClick={() => setEditorOpen(false)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
              >
                Отмена
              </button>
              {!isViewOnly && (
                <>
                {editorMode === 'edit' && editingDocumentStatus === 'POSTED' && (
                  <button
                    onClick={handleUnpostFromEditor}
                    disabled={saving}
                    className="px-3 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-60 inline-flex items-center gap-1"
                  >
                    <RotateCcw size={14} />
                    Снять с проведения
                  </button>
                )}
                {(editorMode === 'edit' || editorMode === 'create') && (
                  <button
                    onClick={handlePostFromEditor}
                    disabled={saving}
                    className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-1"
                  >
                    <Check size={14} />
                    {editorMode === 'create' ? 'Провести' : (editingDocumentStatus === 'POSTED' ? 'Перепровести' : 'Провести')}
                  </button>
                )}
                {isDraftEditable && (
                  <button
                    onClick={saveDocument}
                    disabled={saving}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-60 inline-flex items-center gap-1"
                  >
                    <Save size={14} />
                    Сохранить
                  </button>
                )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerLevelChangeDocuments;
